import React, { useRef } from 'react';
import { Box, Modal, Rating } from '@mui/material';
import BaseTypography from 'core/components/typography/BaseTypography';
import { useDispatch, useSelector } from 'react-redux';
import {
	isMediaFeedbackModalOpen,
	toggleMediaFeedbackModal,
} from 'core/store/middleware/StudioCanvasMiddleware';
import { modalStyles } from 'core/layouts/ui-blocks/ReusableModalStyles';
import { mediaFeedbackStyles } from 'core/components/media-feedback/MediaFeedbackStyles';
import { reusableStyles } from 'core/layouts/ui-blocks/ReusableStyles';
import { ReactComponent as RatingFilled } from 'core/assets/svg/common/rating-filled.svg';
import { ReactComponent as RatingOutline } from 'core/assets/svg/common/rating-outline.svg';
import { ReactComponent as CloseIcon } from 'core/assets/svg/asset-library/close-asset.svg';
import { ReactComponent as CheckedIcon } from 'core/assets/svg/common/round-check.svg';
import BaseButton from 'core/components/button/BaseButton';
import { inputComponentsStyles } from 'core/layouts/components/settings-sidebar/input-components/InputComponentsStyles';
import BaseTextField from 'core/components/textField/BaseTextField';
import { STUDIO, TEXT_TO_IMAGE } from 'core/navigation/Routes';
import { submitStudioMediaFeedbackAsync } from 'core/store/reducers/studioReducer/StudioSlice';
import { submitCanvasMediaFeedbackAsync } from 'core/store/reducers/canvasReducer/CanvasSlice';

export default function MediaFeedback({ mediaId, route = TEXT_TO_IMAGE }) {
	const dispatch = useDispatch();
	const [rating, setRating] = React.useState(0);
	const [comment, setComment] = React.useState('');
	const [feedSubmitted, setFeedSubmitted] = React.useState(false);
	const inputRef = useRef(null);

	const isModalOpen = useSelector((state) => isMediaFeedbackModalOpen(state, route));

	const handleModalClose = (e) => {
		e.stopPropagation();
		dispatch(toggleMediaFeedbackModal(false, route));
		setFeedSubmitted(false);
	};

	const handleSubmitFeedback = (e) => {
		e.stopPropagation();
		const feedbackData = {
			mediaId,
			rating,
			comment,
		};
		if (route === TEXT_TO_IMAGE) {
			dispatch(submitStudioMediaFeedbackAsync(feedbackData));
		} else if (route === STUDIO) {
			dispatch(submitCanvasMediaFeedbackAsync(feedbackData));
		}
		setRating(0);
		setComment('');
		setFeedSubmitted(true);
	};

	return (
		<Modal
			open={isModalOpen}
			onClose={handleModalClose}
			aria-labelledby="media-feedback-modal"
			aria-describedby="Media Feedback"
			sx={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2.5px)' }}
		>
			<Box id="media-feedback" sx={modalStyles.modalWrapper} onClick={(e) => e.stopPropagation()}>
				<Box id="modal-body" sx={modalStyles.modalBody}>
					<Box id="modal-content-wrapper" sx={mediaFeedbackStyles.contentWrapper}>
						{!feedSubmitted && (
							<Box
								id="close-button"
								sx={mediaFeedbackStyles.closeButton}
								onClick={handleModalClose}
							>
								<CloseIcon />
							</Box>
						)}
						{feedSubmitted ? (
							<Box
								id="success-feedback-container"
								sx={[reusableStyles.flexColWithGap(24), mediaFeedbackStyles.successContainer]}
							>
								<Box id="title-cont" sx={[reusableStyles.flexCol, { alignItems: 'center' }]}>
									<Box id="checked-icon" sx={mediaFeedbackStyles.checkedIconCont}>
										<CheckedIcon />
									</Box>
									<BaseTypography variant="b1_medium" color="colors.light3">
										Feedback submitted successfully!
									</BaseTypography>
								</Box>
								<BaseButton
									id="okay-button"
									sx={mediaFeedbackStyles.submitButton}
									onClick={handleModalClose}
								>
									<BaseTypography variant="b1_v2_bold" color="colors.dark6">
										Done
									</BaseTypography>
								</BaseButton>
							</Box>
						) : (
							<Box
								id="submit-feedback-container"
								sx={[reusableStyles.flexColWithGap(12), { alignItems: 'flex-start' }]}
							>
								<Box id="title-container" sx={reusableStyles.flexColWithGap(4)}>
									<BaseTypography variant="b1_v2_medium" color="colors.light3">
										Feedback
									</BaseTypography>
									<BaseTypography variant="b3_v2_medium" color="colors.light3">
										Tell us what you like or dislike about this generation
									</BaseTypography>
								</Box>
								<Box
									sx={[
										inputComponentsStyles.textFieldInputWrapper,
										mediaFeedbackStyles.inputContainerStyles,
									]}
									onKeyDown={(e) => e.stopPropagation()}
								>
									<Box component="form" noValidate autoComplete="off">
										<BaseTextField
											id="feedback-input"
											inputRef={inputRef}
											name="feedback"
											fullWidth
											variant="standard"
											placeholder="Enter your comments here"
											InputProps={{
												disableUnderline: true,
												style: mediaFeedbackStyles.inputStyles,
											}}
											multiline
											maxRows={2}
											value={comment}
											onChange={(event) => setComment(event.target.value)}
										/>
									</Box>
								</Box>
								<Box
									id="rating-section"
									sx={[
										reusableStyles.flexRowWithGap(8),
										{ width: '100%', justifyContent: 'space-between' },
									]}
								>
									<Box id="rating-label" sx={reusableStyles.flexRowWithGap(8)}>
										<BaseTypography variant="b1_v2_regular" color="colors.light3">
											Rate this generation
										</BaseTypography>
										<Box id="rating-container" sx={{ '& > legend': { mt: 2 } }}>
											<Rating
												name="media-rating"
												value={rating}
												icon={<RatingFilled />}
												emptyIcon={<RatingOutline />}
												onChange={(event, newValue) => {
													if (newValue !== null) {
														setRating(newValue);
													} else {
														setRating(0);
													}
												}}
											/>
										</Box>
									</Box>
									<Box id="button-wrapper" sx={mediaFeedbackStyles.buttonWrapper}>
										<BaseButton
											id="submit-feedback"
											disabled={!rating && !comment}
											sx={mediaFeedbackStyles.submitButton}
											onClick={handleSubmitFeedback}
										>
											<BaseTypography
												variant="b1_v2_bold"
												color={!rating && !comment ? 'colors.light5' : 'colors.dark6'}
											>
												Submit
											</BaseTypography>
										</BaseButton>
									</Box>
								</Box>
							</Box>
						)}
					</Box>
				</Box>
			</Box>
		</Modal>
	);
}
